import React, { useEffect, useState } from 'react';
import useBodyClass from 'helpers/hooks/useBodyClass';
import { FormModel, Product, Props, Filters } from './CommissionDetailsPage.types';
import { Box } from 'modules/af_cm_ui_kit';
import { FIELDS, FIELDS_LABELS, COMMISSION_TYPE, FILTER_LABELS, FILTER_NAMES } from '../../fields/types';
import validationSchema from './validationSchema';
import ProductsTable from '../../components/ProductsTable/ProductsTable';
import { COMMISSION_TERM_TYPE_LABEL } from 'modules/commission_terms/api/types';
import { FormProvider, useForm } from 'react-hook-form';
import useYupValidationResolver from 'form/yup/reactHookFormResolver';
import {
  DateInput,
  FormInputWrapper,
  PercentageDecimalInput,
  RadioGroup,
  Select,
  TextInput,
} from 'form/infrastructure/reactHookForm';
import { Icon } from 'lmc-ui-library';
import styled from 'styled-components';
import { mapFiltersBasedOnAvailableProducts } from 'modules/commission_terms/mapper';
import { Option } from 'form/components/Select/Select';

import styles from './CommissionDetailsPage.module.scss';

const CommissionDetailsPage: React.FC<Props> = ({ formData, isEdited = false, availableProducts = [], onSubmit }) => {
  useBodyClass(...styles['new-cm'].split(' '));
  const methods = useForm<FormModel>({
    mode: 'onBlur',
    defaultValues: formData,
    resolver: useYupValidationResolver(validationSchema),
  });
  const {
    getValues,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitted },
  } = methods;
  const [resources, setResources] = useState<Filters>({
    [FILTER_NAMES.PRODUCT_TIER]: [],
    [FILTER_NAMES.PURPOSE_OF_THE_LOAN]: [],
    [FILTER_NAMES.PRODUCT_TYPE]: [],
    [FILTER_NAMES.FIXED_RATE_LENGTH]: [],
  });
  const [visibleProducts, setVisibleProducts] = useState<Product[]>(availableProducts);
  const [watchCommissionType, watchProducts] = watch([FIELDS.COMMISSION_TERM_TYPE, FIELDS.PRODUCTS]);

  const filterProducts = (filters: FormModel['filters']) => {
      let filteredProducts = availableProducts.filter(
        (product: Product) =>
          !filters.productTier || product.tier === filters.productTier || product.productGroup === filters.productTier,
      );

      filteredProducts = filteredProducts.filter(
        (product) => !filters.productType || product.type === filters.productType,
      );

      filteredProducts = filteredProducts.filter(
        (product) => !filters.purposeOfTheLoan || product.loanPurpose === filters.purposeOfTheLoan,
      );

      filteredProducts = filteredProducts.filter(
        (product) =>
          !filters.purposeOfTheLoan ||
          product.loanPurpose === filters.purposeOfTheLoan ||
          // will not add ER to the product types, as it may have unintended effects otherwise
          product.type === 'ER',
      );

      filteredProducts = filteredProducts.filter(
        (product) => !filters.fixedRateLength || product.fixRateTerm === filters.fixedRateLength,
      );

      return filteredProducts;
    };

  useEffect(() => {
    setResources(mapFiltersBasedOnAvailableProducts(availableProducts));
  }, [availableProducts]);

  const handleFilter = (option: Option, filter: `${FIELDS.FILTERS}.${FILTER_NAMES}`) => {
    setValue(filter, option.name as never);
    setVisibleProducts(filterProducts(getValues('filters')));
  };

  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [watchCommissionType, isSubmitted, trigger]);

  const submitForm = handleSubmit(onSubmit);

  const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  `;

  return (
    <FormProvider<FormModel> {...methods}>
      <form onSubmit={submitForm}>
        <div className={styles.wrapper}>
          <div className={styles.heading}>
            <p className={styles.header}>{isEdited ? getValues(FIELDS.COMMISSION_NAME) : 'Commission Create'}</p>
            <button type="submit" className={styles.createButton}>
              {isEdited ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
        <Box title="">
          <h3 className={styles.header}>Commission details</h3>
          <FormInputWrapper title="" name={FIELDS.COMMISSION_TERM_TYPE}>
            <RadioGroup
              name={FIELDS.COMMISSION_TERM_TYPE}
              isDisplayFlex={true}
              disabled={isEdited}
              wrapperStyles={{ gap: '12px' }}
              options={[
                {
                  value: COMMISSION_TYPE.STANDARD,
                  text: COMMISSION_TERM_TYPE_LABEL.STANDARD,
                },
                {
                  value: COMMISSION_TYPE.ENHANCED,
                  text: COMMISSION_TERM_TYPE_LABEL.ENHANCED,
                },
                {
                  value: COMMISSION_TYPE.ONGOING,
                  text: COMMISSION_TERM_TYPE_LABEL.ONGOING,
                },
              ]}
            />
          </FormInputWrapper>
          <div className={styles.mainContainer}>
            <FormInputWrapper title={FIELDS_LABELS.COMMISSION_NAME} name={FIELDS.COMMISSION_NAME}>
              <TextInput />
            </FormInputWrapper>
            {watchCommissionType === COMMISSION_TYPE.STANDARD && (
              <FormInputWrapper title={FIELDS_LABELS.FEE} name={FIELDS.FEE}>
                <PercentageDecimalInput />
              </FormInputWrapper>
            )}

            {watchCommissionType === COMMISSION_TYPE.ENHANCED && (
              <FormInputWrapper title={FIELDS_LABELS.FEE} name={FIELDS.FEE}>
                <PercentageDecimalInput />
              </FormInputWrapper>
            )}

            {watchCommissionType === COMMISSION_TYPE.ONGOING && (
              <div className={styles.subContainer}>
                <FormInputWrapper title={FIELDS_LABELS.UPFRONT_FEE} name={FIELDS.UPFRONT_FEE}>
                  <PercentageDecimalInput />
                </FormInputWrapper>
                <FormInputWrapper title={FIELDS_LABELS.ONGOING_FEE} name={FIELDS.ONGOING_FEE}>
                  <PercentageDecimalInput />
                </FormInputWrapper>
                <FormInputWrapper title={FIELDS_LABELS.FEE_END_DATE} name={FIELDS.FEE_END_DATE}>
                  <DateInput placeholder={'DD/MM/YYYY'} />
                </FormInputWrapper>
              </div>
            )}
          </div>
          <div className={styles.tableContainer}>
            <h3 className={styles.header}>Applicable products</h3>

            <div className={styles.fitlersContainer}>
              <FormInputWrapper
                title={FILTER_LABELS.PURCHASE_TYPE}
                name={`${FIELDS.FILTERS}.${FILTER_NAMES.PURPOSE_OF_THE_LOAN}`}
                style={{ margin: '0 0' }}>
                <Select
                  options={resources.purposeOfTheLoan}
                  nullable={true}
                  onOptionClick={(option) => handleFilter(option, 'filters.purposeOfTheLoan')}
                  searchable={false}
                  style={{ fontWeight: 'normal' }}
                  onSearchInputChange={() => {}}
                  rightIcon={(stateColor, setFocused, isFocused) => (
                    <IconWrapper onClick={() => (isFocused ? setFocused(false) : setFocused(true))}>
                      <Icon type={isFocused ? 'iconUp' : 'iconDown'} color={'#2d3540'} />
                    </IconWrapper>
                  )}
                />
              </FormInputWrapper>

              <FormInputWrapper
                title={FILTER_LABELS.PRODUCT_TYPE}
                name={`${FIELDS.FILTERS}.${FILTER_NAMES.PRODUCT_TYPE}`}
                style={{ margin: '0 0' }}>
                <Select
                  options={resources.productType}
                  nullable={true}
                  onOptionClick={(option) => handleFilter(option, 'filters.productType')}
                  searchable={false}
                  style={{ fontWeight: 'normal' }}
                  onSearchInputChange={() => {}}
                  rightIcon={(stateColor, setFocused, isFocused) => (
                    <IconWrapper onClick={() => (isFocused ? setFocused(false) : setFocused(true))}>
                      <Icon type={isFocused ? 'iconUp' : 'iconDown'} color={'#2d3540'} />
                    </IconWrapper>
                  )}
                />
              </FormInputWrapper>

              <FormInputWrapper
                title={FILTER_LABELS.PRODUCT_TIER}
                name={`${FIELDS.FILTERS}.${FILTER_NAMES.PRODUCT_TIER}`}
                style={{ margin: '0 0' }}>
                <Select
                  options={resources.productTier}
                  nullable={true}
                  onOptionClick={(option) => handleFilter(option, 'filters.productTier')}
                  searchable={false}
                  style={{ fontWeight: 'normal' }}
                  onSearchInputChange={() => {}}
                  rightIcon={(stateColor, setFocused, isFocused) => (
                    <IconWrapper onClick={() => (isFocused ? setFocused(false) : setFocused(true))}>
                      <Icon type={isFocused ? 'iconUp' : 'iconDown'} color={'#2d3540'} />
                    </IconWrapper>
                  )}
                />
              </FormInputWrapper>

              <FormInputWrapper
                title={FILTER_LABELS.FIXED_RATE_TERM}
                name={`${FIELDS.FILTERS}.${FILTER_NAMES.FIXED_RATE_LENGTH}`}
                style={{ margin: '0 0' }}>
                <Select
                  options={resources.fixedRateLength}
                  nullable={true}
                  onOptionClick={(option) => handleFilter(option, 'filters.fixedRateLength')}
                  searchable={false}
                  style={{ fontWeight: 'normal' }}
                  onSearchInputChange={() => {}}
                  rightIcon={(stateColor, setFocused, isFocused) => (
                    <IconWrapper onClick={() => (isFocused ? setFocused(false) : setFocused(true))}>
                      <Icon type={isFocused ? 'iconUp' : 'iconDown'} color={'#2d3540'} />
                    </IconWrapper>
                  )}
                />
              </FormInputWrapper>
            </div>

            <ProductsTable
              selectedItems={watchProducts}
              items={visibleProducts}
              onItemsSelected={(selectedProducts) => setValue(FIELDS.PRODUCTS, selectedProducts)}
            />
            {errors[FIELDS.PRODUCTS] && <div className="error-message coral">{errors[FIELDS.PRODUCTS]}</div>}
          </div>
        </Box>
      </form>
    </FormProvider>
  );
};

export default CommissionDetailsPage;
